/**
 * Google Identity Services token client, browser-only.
 *
 * A static site has no server to hold a client secret, so this is the implicit/token flow:
 * an access token lives in memory for ~1 hour and there is no refresh token. See PLAN.md §2.
 */

export const PICKER_SCOPE = 'https://www.googleapis.com/auth/photospicker.mediaitems.readonly';

const GIS_SRC = 'https://accounts.google.com/gsi/client';

interface TokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface TokenClient {
  requestAccessToken(overrides?: { prompt?: string }): void;
}

interface GoogleAccounts {
  accounts: {
    oauth2: {
      initTokenClient(config: {
        client_id: string;
        scope: string;
        callback: (response: TokenResponse) => void;
        error_callback?: (error: { type?: string; message?: string }) => void;
      }): TokenClient;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleAccounts;
  }
}

let loader: Promise<GoogleAccounts> | null = null;

export function loadGoogleIdentity(): Promise<GoogleAccounts> {
  if (window.google?.accounts?.oauth2) return Promise.resolve(window.google);
  if (loader) return loader;

  loader = new Promise<GoogleAccounts>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
    const script = existing ?? document.createElement('script');

    const onLoad = () => {
      if (window.google?.accounts?.oauth2) resolve(window.google);
      else reject(new Error('Google Identity Services loaded but did not expose accounts.oauth2'));
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', () => {
      loader = null;
      reject(new Error('Failed to load Google Identity Services (network or blocked by an extension)'));
    });

    if (!existing) {
      script.src = GIS_SRC;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return loader;
}

export interface AccessToken {
  token: string;
  /** Epoch ms. Google issues ~1 hour; the gallery must handle the cliff (PLAN.md §7 Q8). */
  expiresAt: number;
}

/**
 * Must be called from a user gesture — it opens a popup, which browsers otherwise block.
 */
export async function requestAccessToken(clientId: string, scope = PICKER_SCOPE): Promise<AccessToken> {
  const google = await loadGoogleIdentity();

  return new Promise<AccessToken>((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope,
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(new Error(response.error_description || response.error || 'Authorization failed'));
          return;
        }
        resolve({
          token: response.access_token,
          expiresAt: Date.now() + (response.expires_in ?? 3600) * 1000,
        });
      },
      error_callback: (error) => {
        reject(new Error(error.message || error.type || 'Authorization was closed or blocked'));
      },
    });

    client.requestAccessToken();
  });
}
